"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { workScopeStatusOptions } from "../../constants/options"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const BASE_FIELD_NAME = "home.muckGutInfo"

function HomeMuckGutForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillMuckGutNeededOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillMuckGutNeededOn` })
    
    // Watch status field and claimed by field
    const muckGutNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.muckGutNeeded` })
    const claimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const goodwillMuckGutNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillMuckGutNeeded` })
    const goodwillMuckGutNeededClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillMuckGutNeededClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: muckGutNeeded,
        claimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillMuckGutNeeded,
        claimedBy: goodwillMuckGutNeededClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillMuckGutNeededClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillMuckGutNeededOn,
    })

    return (
        <HomeDetailsWrapperAccordion heading="Muck & Gut" wrapperClassName="two-cols-grid">
            <WorkScopeStatusSelect workScopeSlug="muck-and-gut-interior"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Muck & Gut Needed" toggleName={`${BASE_FIELD_NAME}.goodwillMuckGutNeededOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.muckGutNeeded`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillMuckGutNeededOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Muck & Gut Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillMuckGutNeeded`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Muck & Gut Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillMuckGutNeededClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <FormTextInput
                inputGroupProps={{ label: "Height of wall removal (in inches)" }}
                placeholder="Height of wall removal (in inches)"
                type="number"
                name={`${BASE_FIELD_NAME}.heightOfWallRemoval`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{ label: "Is the ceiling removed?" }}
                name={`${BASE_FIELD_NAME}.ceilingRemoved`}
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{ label: "Give a brief description of the gutting status including what remains to be done", wrapperClassName: "two-cols" }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeMuckGutForm
